/**
 * Phone Today screen (docs/mobile-design.md §4.1-4.2), shared by mobile web and React Native: the `/me/today` payload,
 * the clock card state machine, on-site / off-site wording, what a blocked clock-in looks like, and which cards show
 * in what order. Pure TS.
 */
import { entryMinutes } from '../workforce/time';
import type { CurrentTime, Shift } from '../workforce/types';
import { formatDistance, type Geofence, type GeoStatus } from '../workforce/clock';
import type { FeedItem } from '../home/feed';
import { activityText, issueActivity } from '../home/issueActivity';
import type { LiveStatus } from '../workforce/attendance';
import { formatInstantGB } from '../gbDate';
import { DateTime } from 'luxon';

export interface TodayTask { id: string; key: string | null; title: string; due_date: string; overdue: boolean; project_id: string | null; is_private: boolean }
/** t361u: an assigned training course not passed yet (archived courses left out). `id` is the assignment. */
export interface TodayTraining { id: string; course_id: string; title: string; due_on: string | null; overdue: boolean }
export interface TodayChat { id: string; kind: string; name: string; unread_count: number; mention_count: number; last_message_at: string | null }
export interface TodayApprovals { swaps: number; /** t228u */ drops?: number; claims: number; leave: number; timesheets: number; total: number }
export type WhosInCounts = Partial<Record<LiveStatus | 'scheduled_any', number>>;

export interface MeToday {
  date: string;
  timezone: string;
  generated_at: string;
  /** The time clock: what /workforce/time/current returns plus the target building's fence. */
  clock: CurrentTime & { location_id: string | null; location_name: string | null; geofence: Geofence | null };
  /** t369u: the Time clock module is switched off for the workspace: no clock card or clock buttons. */
  clock_off?: boolean;
  /** The shift you're on now, else the next one. */
  shift: { state: 'now' | 'next'; shift: Shift; location_name: string | null } | null;
  needs_me: {
    tasks: TodayTask[]; tasks_total: number; acks: number; mentions: TodayChat[];
    /** t361u: outstanding training, soonest due first (missing from APIs before t361u). */
    training?: TodayTraining[]; training_total?: number;
  };
  /** Only for people who approve something; counts follow the same scopes as the pages. */
  manager: { approvals: TodayApprovals; whos_in: WhosInCounts | null } | null;
  unread_chats: TodayChat[];
  unread_chats_total: number;
  feed: FeedItem[];
}

// ------------------------------------------------------------------------------------------------ clock card
export type ClockState = 'out' | 'in' | 'break';
export type ClockAction = 'in' | 'out' | 'break' | 'resume';
export interface ClockCard {
  state: ClockState;
  /** The big one-tap button. */
  primary: ClockAction;
  /** Break or end break, when clocked in. */
  secondary: ClockAction | null;
  since: string | null;
  worked_minutes: number;
}
export function clockCard(c: Pick<CurrentTime, 'entry'>, now = Date.now()): ClockCard {
  const e = c.entry;
  if (!e) return { state: 'out', primary: 'in', secondary: null, since: null, worked_minutes: 0 };
  const onBreak = e.breaks.some(b => !b.ended_at);
  return {
    state: onBreak ? 'break' : 'in', primary: onBreak ? 'resume' : 'out', secondary: onBreak ? 'out' : 'break',
    since: e.clock_in_at, worked_minutes: entryMinutes(e, now).paid_minutes,
  };
}
export const CLOCK_ACTION_LABEL: Record<ClockAction, string> = { in: 'Clock in', out: 'Clock out', break: 'Start break', resume: 'End break' };

/** On site / Off site with the distance, for the clock card. Null when the building doesn't check location. */
export function geoLabel(geo: GeoStatus | null | undefined, distance: number | null | undefined): { tone: 'ok' | 'warn'; text: string } | null {
  if (geo === 'inside') return { tone: 'ok', text: distance != null ? `On site · ${formatDistance(distance)}` : 'On site' };
  if (geo === 'outside') return { tone: 'warn', text: distance != null ? `Off site · ${formatDistance(distance)}` : 'Off site' };
  if (geo === 'unknown') return { tone: 'warn', text: 'Location not confirmed' };
  if (geo === 'kiosk') return { tone: 'ok', text: 'Kiosk' };
  return null;
}

/**
 * A clock-in the location check refused (enforced fence): the sheet offers Retry and Use the kiosk. Clock-out is
 * never blocked, so only 'in' maps. `failure` is the device's reason when there was no reading at all.
 */
export interface ClockBlock { reason: 'outside' | 'no_location'; distance_m: number | null; message: string }
export function clockBlock(action: ClockAction, err: { status?: number; body?: unknown } | null | undefined): ClockBlock | null {
  if (action !== 'in' || !err || err.status !== 403) return null;
  const b = (err.body ?? {}) as { code?: string; message?: string; geo?: GeoStatus; distance_m?: number | null };
  if (b.code !== 'outside_geofence') return null;
  return { reason: b.geo === 'outside' ? 'outside' : 'no_location', distance_m: b.distance_m ?? null, message: b.message ?? 'You need to be at the building to clock in' };
}

// ------------------------------------------------------------------------------------------------ cards and order
export type TodayCard = 'clock' | 'shift' | 'needs_me' | 'approvals' | 'whos_in' | 'chats' | 'feed';
/** Cards top to bottom; empty ones are left out (the clock always shows). */
export function todayCards(t: MeToday): TodayCard[] {
  const out: TodayCard[] = t.clock_off ? [] : ['clock'];
  if (t.shift) out.push('shift');
  if (t.needs_me.tasks_total || t.needs_me.acks || t.needs_me.mentions.length || t.needs_me.training_total) out.push('needs_me');
  if (t.manager?.approvals.total) out.push('approvals');
  if (t.manager?.whos_in && (t.manager.whos_in.scheduled_any ?? 0) > 0) out.push('whos_in');
  if (t.unread_chats.length) out.push('chats');
  if (t.feed.length) out.push('feed');
  return out;
}

/**
 * t361u: the dated rows at the top of Needs me: tasks due (today or overdue) and outstanding training, by due date, so
 * the overdue ones come first; undated rows go after the dated ones. Ties keep tasks before training.
 */
export type NeedsMeRow = { kind: 'task'; id: string; due: string | null; task: TodayTask } | { kind: 'training'; id: string; due: string | null; training: TodayTraining };
export function needsMeRows(n: MeToday['needs_me']): NeedsMeRow[] {
  const rows: NeedsMeRow[] = [
    ...n.tasks.map(task => ({ kind: 'task' as const, id: task.id, due: task.due_date || null, task })),
    ...(n.training ?? []).map(training => ({ kind: 'training' as const, id: training.id, due: training.due_on || null, training })),
  ];
  return rows.map((r, i) => ({ r, i })).sort((a, b) => (a.r.due ? 0 : 1) - (b.r.due ? 0 : 1) || (a.r.due && b.r.due ? a.r.due.localeCompare(b.r.due) : 0) || a.i - b.i).map(x => x.r);
}
/** Outstanding training past what Needs me lists ("More training"). */
export const moreTraining = (n: MeToday['needs_me']) => Math.max(0, (n.training_total ?? 0) - (n.training?.length ?? 0));

/**
 * t361u: a training row's due text on All actions: "Due today", "Due Fri 30 Oct", "Overdue · was due 20 Oct" (the year
 * only when it isn't this year); null with no due date. `today` is the workspace's date (MeToday.date).
 */
export function trainingDueLabel(dueOn: string | null | undefined, today: string): { text: string; overdue: boolean } | null {
  if (!dueOn) return null;
  const d = DateTime.fromISO(dueOn).setLocale('en-GB');
  if (!d.isValid) return null;
  const yr = d.year !== DateTime.fromISO(today).year ? ' yyyy' : '';
  if (dueOn < today) return { text: `Overdue · was due ${d.toFormat(`d LLL${yr}`)}`, overdue: true };
  if (dueOn === today) return { text: 'Due today', overdue: false };
  return { text: `Due ${d.toFormat(`ccc d LLL${yr}`)}`, overdue: false };
}

/** Who's in, as the short rows the card shows (zero counts left out). */
export const WHOS_IN_ROWS: { id: keyof WhosInCounts; label: string; tone: 'ok' | 'warn' | 'bad' | 'none' }[] = [
  { id: 'working', label: 'In', tone: 'ok' }, { id: 'on_break', label: 'On break', tone: 'none' }, { id: 'late', label: 'Late', tone: 'warn' },
  { id: 'no_show', label: 'Not in', tone: 'bad' }, { id: 'scheduled', label: 'Later', tone: 'none' }, { id: 'on_leave', label: 'Off', tone: 'none' },
];

/** A conversation's name as people see it: #channel, the DM's other people, or the room title. */
export function conversationDisplayName(c: { kind: string; title: string | null; slug: string | null; members?: { user_id: string; display_name: string }[] | null }, me?: string | null): string {
  if (c.kind === 'channel') return `#${c.slug ?? c.title ?? 'channel'}`;
  if (c.title) return c.title;
  const others = (c.members ?? []).filter(m => m.user_id !== me).map(m => m.display_name);
  return others.length ? others.join(', ') : 'Just you';
}

// ------------------------------------------------------------------------------------------------ compact feed
/** One feed item as a single compact row (the full card lives in the feed). */
export interface FeedLine { kind: FeedItem['kind']; title: string; detail: string; href: string; comments: number }
export function feedLine(i: FeedItem): FeedLine {
  const comments = 'thread' in i && i.thread && i.kind !== 'discussion' ? i.thread.count : 0;
  switch (i.kind) {
    case 'announcement': return { kind: i.kind, title: i.announcement.title, detail: i.announcement.author_name, href: `/announcements/${i.announcement.id}`, comments };
    case 'kudos': case 'birthday': {
      const to = i.kudos.recipients.map(r => r.display_name).join(', ') || i.kudos.teams.map(t => t.name).join(', ');
      return { kind: i.kind, title: i.kind === 'birthday' ? `Happy birthday, ${to}` : `Kudos to ${to}`, detail: i.kudos.body.slice(0, 80), href: `/kudos/${i.kudos.id}`, comments };
    }
    case 'ack': return { kind: i.kind, title: i.ack.preview, detail: `To acknowledge · ${i.ack.conversation_name}`, href: `/acknowledgements/${i.ack.message_id}`, comments: 0 };
    case 'shift_offer': return { kind: i.kind, title: 'Shift offered to you', detail: [i.offer.shift.location_name, i.offer.shift.position_name].filter(Boolean).join(' · '), href: `/workforce/offers/${i.offer.id}`, comments: 0 };
    case 'task_completed': return { kind: i.kind, title: i.task.title, detail: `Done${i.task.actor_name ? ` by ${i.task.actor_name}` : ''}`, href: i.task.href, comments };
    case 'issue': return { kind: i.kind, title: `#${i.issue.ref} ${i.issue.title}`, detail: [activityText(issueActivity(i, { withComment: true })), i.issue.location_name].filter(Boolean).join(' · '), href: `/incidents/${i.issue.id}`, comments };
    case 'event': return { kind: i.kind, title: i.event.title, detail: i.reason === 'invited' ? 'You\'re invited' : 'Event', href: `/calendar/events/${i.event.id}`, comments };
    case 'discussion': return { kind: i.kind, title: i.thread.title, detail: `${i.thread.count} new comment${i.thread.count === 1 ? '' : 's'}`, href: i.thread.link, comments: 0 };
  }
}

// ------------------------------------------------------------------------------------------------ Today strip (Home, t182u)
/** A count chip on the Today strip pinned at the top of Home: "what's waiting on me". */
export type StripChipId = 'tasks' | 'acks' | 'training' | 'approvals' | 'hr' | 'run' | 'mentions';
export interface StripChip { id: StripChipId; label: string; n: number; to: string }
/** The waiting-on-me chips, most urgent first; zero counts are left out. `runsToday` only for drivers; `hrApprovals` (t413u) only for HR approvers. */
export function todayStripChips(t: MeToday, o: { runsToday?: number; approvalsTo?: string; hrApprovals?: number } = {}): StripChip[] {
  const mentions = t.needs_me.mentions.reduce((n, c) => n + c.mention_count, 0);
  const all: StripChip[] = [
    { id: 'run', label: o.runsToday === 1 ? 'Run today' : 'Runs today', n: o.runsToday ?? 0, to: '/fleet/my-runs' },
    { id: 'approvals', label: 'To approve', n: t.manager?.approvals.total ?? 0, to: o.approvalsTo ?? '/rota/approvals' },
    { id: 'hr', label: 'HR to approve', n: o.hrApprovals ?? 0, to: '/settings/people/hr-changes' },
    { id: 'tasks', label: t.needs_me.tasks_total === 1 ? 'Task due' : 'Tasks due', n: t.needs_me.tasks_total, to: '/tasks' },
    { id: 'acks', label: 'To acknowledge', n: t.needs_me.acks, to: '/acknowledgements' },
    // t361u: outstanding training, as All actions lists it.
    { id: 'training', label: 'Training', n: t.needs_me.training_total ?? 0, to: '/training' },
    { id: 'mentions', label: mentions === 1 ? 'Mention' : 'Mentions', n: mentions, to: '/chat?filter=mentions' },
  ];
  return all.filter(c => c.n > 0);
}
/** The strip's shift line: "Now" or "Next", with the day when it isn't today. */
export function stripShiftLabel(s: NonNullable<MeToday['shift']>, today: string): string {
  if (s.state === 'now') return 'Now';
  return s.shift.local_date === today ? 'Next · Today' : 'Next';
}

// ------------------------------------------------------------------------------------------------ t184u: top bar shift
/**
 * The shift status in the phone top bar (every tab): the shift on now, else the next one, as a compact time and
 * building, and one clock button. Clocked in: the elapsed (paid) time, building, Clock out and (t187u) a compact
 * Start break. On a break: the break's running time ("Break 12m") and End break.
 * Not clocked in: Clock in when a clock-in now would count (a shift in the early window, today's shift, or no shift
 * but somewhere to clock in, unscheduled, whatever the next shift's day); a later day's shift with nowhere to clock in
 * has no button. Null when there's nothing to show.
 */
export interface TopBarShift {
  state: ClockState;
  /** First line: "06:00–14:00", "Wed 06:00–14:00", or the elapsed "2h 05m". */
  time: string;
  /** Second line, truncated first: the building ("On break" while on one). */
  place: string | null;
  /** The building behind `place`, so the name opens its page. */
  placeId?: string | null;
  shiftId: string | null;
  action: ClockAction | null;
  /** t187u: the compact second action next to it (Start break while clocked in). */
  secondary: ClockAction | null;
}
const hhmm = (iso: string, zone: string) => formatInstantGB(iso, 'HH:mm', zone);
const dow = (iso: string, zone: string) => formatInstantGB(iso, 'ccc', zone);
/** A break's running time: "12m", "1h 05m". */
export const breakLabel = (min: number) => (min < 60 ? `${min}m` : elapsedLabel(min));
export const elapsedLabel = (min: number) => `${Math.floor(min / 60)}h ${String(Math.max(0, min % 60)).padStart(2, '0')}m`;

export function topBarShift(t: Pick<MeToday, 'clock' | 'shift' | 'date' | 'clock_off'>, now = Date.now(), zone = 'Europe/London'): TopBarShift | null {
  const c = t.clock;
  // t369u: Time clock off: just the shift (no clock button), or nothing.
  if (t.clock_off) {
    const s = t.shift;
    if (!s) return null;
    const times = `${hhmm(s.shift.starts_at, zone)}–${hhmm(s.shift.ends_at, zone)}`;
    return { state: 'out', time: s.shift.local_date === t.date ? times : `${dow(s.shift.starts_at, zone)} ${times}`, place: s.location_name, placeId: s.location_name ? s.shift.location_id : null, shiftId: s.shift.id, action: null, secondary: null };
  }
  const card = clockCard(c, now);
  const s = t.shift;
  const place = s?.location_name ?? c.location_name ?? null;
  const placeId = s?.location_name ? s.shift.location_id : c.location_name ? c.location_id : null;
  if (card.state !== 'out') {
    const shiftId = c.entry?.shift_id ?? s?.shift.id ?? null;
    if (card.state === 'break') {
      const open = c.entry!.breaks.find(b => !b.ended_at)!;
      return { state: 'break', time: `Break ${breakLabel(Math.max(0, Math.floor((now - Date.parse(open.started_at)) / 60_000)))}`, place: 'On break', shiftId, action: 'resume', secondary: null };
    }
    return { state: card.state, time: elapsedLabel(card.worked_minutes), place, placeId, shiftId, action: card.primary, secondary: 'break' };
  }
  if (!s) return c.clock_locations.length ? { state: 'out', time: 'No shift', place: c.location_name ?? (c.clock_locations.length === 1 ? c.clock_locations[0]!.name : null), placeId: c.location_name ? c.location_id : c.clock_locations.length === 1 ? c.clock_locations[0]!.id : null, shiftId: null, action: 'in', secondary: null } : null;
  const times = `${hhmm(s.shift.starts_at, zone)}–${hhmm(s.shift.ends_at, zone)}`;
  const today = s.shift.local_date === t.date;
  // A later day's shift still offers Clock in when there's somewhere to clock in (unscheduled), as the Today card does.
  const canIn = Boolean(c.clock_shift_id) || s.state === 'now' || today || c.clock_locations.length > 0;
  return { state: 'out', time: today ? times : `${dow(s.shift.starts_at, zone)} ${times}`, place, placeId, shiftId: s.shift.id, action: canIn ? 'in' : null, secondary: null };
}
